import { Grid, Paper, Typography, Box, Skeleton } from '@mui/material';
import { Users, MessageCircle, Star, Calendar, TrendingUp, Mail } from 'lucide-react';
import { useDashboard } from '../../hooks/dashboard/useDashboard';
import { useEffect, useState } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

import { Suspense, useMemo } from 'react';
import { CircularProgress } from '@mui/material';
import TokenUsageBarChart from '../../components/dashboard/TokenUsageBarChart';
import MonthlySpendPieChart from '../../components/dashboard/MonthlySpendPieChart';
import SpendSummaryTable from '../../components/dashboard/SpendSummaryTable';
import { Token } from '../../types/dashboard';
import { useCacxiStripe } from '../../hooks/dashboard/useStripe';
import { NotificationT, SEVERITY } from '../../types/notification';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Notifications from '../../components/common/Notifications';
import TokenInfoPanel from '../../components/dashboard/TokenInfoPanel';

const StatCard = ({ title, cardName, newValue, totalValue, icon: Icon, color, isLoading }: any) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={1}
      sx={{
        px: 4,
        py: 3,
        height: '100%',
        borderRadius: 2,
        transition: 'all 200ms cubic-bezier(0.2, 0, 0, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ flex: 1 }}>
          {isLoading ? (
            <Skeleton variant="text" width={140} height={24} sx={{ borderRadius: 1 }} />
          ) : (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                mb: 2,
                color: theme.palette.text.secondary,
                letterSpacing: '0.01em',
              }}
            >
              {title}
            </Typography>
          )}
          {isLoading ? (
            <Skeleton variant="text" width={80} height={48} sx={{ borderRadius: 1 }} />
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <Typography
                  variant={title === 'Top Frequent Services' ? 'h4' : 'h3'}
                  sx={{
                    lineHeight: 1,
                    fontWeight: 400,
                    color: theme.palette.text.primary,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {newValue}
                </Typography>

                {newValue > 0 && totalValue > 0 && (
                  <Box
                    sx={{
                      backgroundColor: alpha(theme.palette.success.main, 0.12),
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: theme.palette.success.main,
                      py: 0.5,
                      px: 1,
                      borderRadius: '16px',
                    }}
                  >
                    <TrendingUp size={12} />
                    <Typography
                      variant="caption"
                      sx={{
                        lineHeight: 1,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        color: theme.palette.success.main,
                      }}
                    >
                      {Math.round((100 * newValue) / totalValue)}%
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
          {isLoading ? (
            <Skeleton variant="text" width={100} height={16} sx={{ borderRadius: 1 }} />
          ) : (
            totalValue > 0 && (
              <Typography
                color="text.secondary"
                variant="caption"
                sx={{ fontSize: '0.75rem', fontWeight: 400, mt: 1, display: 'block' }}
              >
                Total {cardName}: {totalValue}
              </Typography>
            )
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: alpha(color, 0.12),
            borderRadius: '12px',
            p: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ml: 2,
          }}
        >
          {isLoading ? (
            <Skeleton variant="circular" width={24} height={24} />
          ) : (
            <Icon size={24} style={{ color }} />
          )}
        </Box>
      </Box>
    </Paper>
  );
};

const StatCardServices = ({ title, newValue, icon: Icon, color, isLoading }: any) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={1}
      sx={{
        px: 4,
        py: 3,
        height: '100%',
        borderRadius: 2,
        transition: 'all 200ms cubic-bezier(0.2, 0, 0, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ flex: 1 }}>
          {isLoading ? (
            <Skeleton variant="text" width={140} height={24} sx={{ borderRadius: 1 }} />
          ) : (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                mb: 2,
                color: theme.palette.text.secondary,
                letterSpacing: '0.01em',
              }}
            >
              {title}
            </Typography>
          )}
          {isLoading ? (
            <>
              {[1, 2, 3].map((_, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Skeleton variant="text" width={100} height={20} sx={{ borderRadius: 1 }} />
                  <Skeleton
                    variant="rounded"
                    width={28}
                    height={20}
                    sx={{ borderRadius: '10px' }}
                  />
                </Box>
              ))}
            </>
          ) : (
            <>
              {newValue?.map(([service, count]: [string, number], index: number) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1.25,
                    py: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      lineHeight: 1.4,
                      fontWeight: 400,
                      color: theme.palette.text.primary,
                      flex: 1,
                    }}
                  >
                    {service}
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.12),
                      minWidth: 28,
                      height: 22,
                      borderRadius: '11px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 500,
                        color: theme.palette.primary.main,
                        fontSize: '0.75rem',
                      }}
                    >
                      {count}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: alpha(color, 0.12),
            borderRadius: '12px',
            p: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ml: 2,
          }}
        >
          {isLoading ? (
            <Skeleton variant="circular" width={24} height={24} />
          ) : (
            <Icon size={24} style={{ color }} />
          )}
        </Box>
      </Box>
    </Paper>
  );
};

const DashboardView = () => {
  const { dashboardData, isLoading, refetch } = useDashboard();
  const organization = useSelector((state: RootState) => state.organizations.selectedOrganisation);
  const currentPlanId = organization?.planId || '';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  // console.log('Dashboard Data:', dashboardData);

  // Check for success param when component mounts
  useEffect(() => {
    const success = searchParams.get('credits');
    if (success === 'success') {
      setNotification({
        open: true,
        message: 'Credits added successfully',
        severity: SEVERITY.SUCCESS,
      });
      // Clear the success param from URL
      navigate('/dashboard', { replace: true });
    } else if (success === 'cancel') {
      setNotification({
        open: true,
        message: 'Failed to add credits',
        severity: SEVERITY.ERROR,
      });
      navigate('/dashboard', { replace: true });
    }
  }, [navigate, searchParams]);

  const { recurrencePlans } = useCacxiStripe();

  const currentPlan = useMemo(() => {
    return [...recurrencePlans].find(plan => plan.id === currentPlanId);
  }, [recurrencePlans, currentPlanId]);

  useEffect(() => {
    refetch();
  }, [refetch, organization]);

  const barChartData = useMemo(() => {
    if (!dashboardData?.totalTokens?.length) return [];
    return dashboardData.totalTokens.map((token: Token) => ({
      month: token.uuid,
      input: token.inputToken,
      output: token.outputToken,
    }));
  }, [dashboardData]);

  const spendSummary = useMemo(() => {
    const basePrice = currentPlan?.unit_amount ? currentPlan.unit_amount / 100 : 0;

    if (!dashboardData?.totalTokens?.length || !organization) {
      return {
        basePrice,
        inputCost: 0,
        outputCost: 0,
        totalSpend: basePrice,
      };
    }

    const tokens = dashboardData.totalTokens[0];
    const extraInputTokens = Math.max(tokens.inputToken, 0);
    const extraOutputTokens = Math.max(tokens.outputToken, 0);
    const inputCost = (extraInputTokens / 1000000) * 4;
    const outputCost = (extraOutputTokens / 1000000) * 12;
    const totalSpend = basePrice + inputCost + outputCost;

    return {
      basePrice,
      inputCost,
      outputCost,
      totalSpend,
    };
  }, [dashboardData, organization, currentPlan]);

  const pieChartData = useMemo(() => {
    return [
      { name: 'Subscription Charge', value: spendSummary.basePrice },
      {
        name: 'Token Usage',
        value: Math.round((spendSummary.inputCost + spendSummary.outputCost) * 1000) / 1000,
      },
    ];
  }, [spendSummary]);

  const stats = dashboardData
    ? [
        organization?.isBookingEnabled
          ? {
              title: 'Customers',
              newValue: dashboardData?.data?.customers?.newCustomers || 0,
              totalValue: dashboardData?.data?.customers?.totalCustomers || 0,
              icon: Users,
              color: '#1a73e8', // Google Blue
            }
          : null,
        organization?.isBookingEnabled
          ? {
              title: 'Bookings',
              newValue: dashboardData?.data?.bookings?.upcomingBookings || 0,
              totalValue: dashboardData?.data?.bookings?.totalBookings || 0,
              icon: Calendar,
              color: '#f9ab00', // Google Yellow
            }
          : null,
        {
          title: 'Chats',
          newValue: dashboardData?.chatlogs?.unread || 0,
          totalValue: dashboardData?.chatlogs?.total || 0,
          icon: MessageCircle,
          color: '#00897b', // Google Teal
        },
      ].filter(Boolean)
    : [];

  const frequentServices =
    dashboardData?.data?.frequentServices &&
    Object.keys(dashboardData?.data.frequentServices).length > 0
      ? (Object.entries(dashboardData?.data.frequentServices) as [string, number][])
          .sort(([_, countA], [__, countB]) => countB - countA)
          .slice(0, 3)
      : [['No data available', 0]];

  const smsCounts = dashboardData?.data?.sms?.smsCountsForDateRange
    ? (Object.entries(dashboardData.data.sms.smsCountsForDateRange) as [string, number][])
        .sort(([_, countA], [__, countB]) => countB - countA)
        .slice(0, 5)
    : [['No data available', 0]];

  const emailCounts = dashboardData?.data?.email?.emailCountsForDateRange
    ? (Object.entries(dashboardData.data.email.emailCountsForDateRange) as [string, number][])
        .sort(([_, countA], [__, countB]) => countB - countA)
        .slice(0, 5)
    : [['No data available', 0]];

  if (isLoading || !dashboardData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8} xl={7}>
          <Suspense fallback={<CircularProgress />}>
            <Paper
              elevation={0}
              sx={{
                px: 3.5,
                py: 4,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'stretch',
              }}
            >
              <MonthlySpendPieChart
                usageData={{
                  totalCredits: dashboardData?.credits?.total || 0,
                  remainingCredits: dashboardData?.credits?.remaining || 0,
                  creditsUsed: dashboardData?.credits?.used || 0,
                }}
              />

              {organization?.isBookingEnabled ? (
                <SpendSummaryTable
                  totalTokens={dashboardData?.totalTokens || []}
                  sms={dashboardData?.data?.sms || { totalSMSCount: 0 }}
                  email={dashboardData?.data?.email || { totalEmailsCount: 0 }}
                  credits={dashboardData?.credits || { total: 0, used: 0, remaining: 0 }}
                  bookingEnabled={organization?.isBookingEnabled || false}
                />
              ) : (
                <SpendSummaryTable
                  totalTokens={dashboardData?.totalTokens || []}
                  sms={{ totalSMSCount: 0 }}
                  email={{ totalEmailCount: 0 }}
                  credits={dashboardData?.credits || { total: 0, used: 0, remaining: 0 }}
                  bookingEnabled={organization?.isBookingEnabled || false}
                />
              )}
            </Paper>
          </Suspense>
        </Grid>

        <Grid item xs={12} md={4} xl={5}>
          <Suspense fallback={<CircularProgress />}>
            <Paper elevation={0} sx={{ px: 3.5, py: 2.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, textSpacing: -1 }}>
                Token Usage Explained
              </Typography>
              <TokenInfoPanel
                plan={organization?.isBookingEnabled || false}
                isTrailEnabled={organization?.isTrailEnabled || false}
              />
            </Paper>
          </Suspense>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        {(isLoading || stats.length === 0 ? Array.from(new Array(3)) : stats).map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} xl={3} key={stat?.title || index}>
            <StatCard
              title={stat?.title ? `New ${stat?.title}` : ''}
              cardName={stat?.title || ''}
              newValue={stat?.newValue || '-'}
              totalValue={stat?.totalValue || 0}
              icon={stat?.icon || Users}
              color={stat?.color || '#ccc'}
              isLoading={isLoading}
            />
          </Grid>
        ))}

        {organization?.isBookingEnabled && (
          <Grid item xs={12} sm={6} md={4} xl={3}>
            <StatCardServices
              title="Top Frequent Services"
              newValue={frequentServices}
              icon={Star}
              color="#ff5722"
              isLoading={isLoading}
            />
          </Grid>
        )}
        {organization?.isBookingEnabled && (
          <Grid item xs={12} sm={6} md={4} xl={3}>
            <StatCardServices
              title="SMS Sent"
              newValue={smsCounts}
              icon={Mail}
              color="#4caf50"
              isLoading={isLoading}
            />
          </Grid>
        )}
        {organization?.isBookingEnabled && (
          <Grid item xs={12} sm={6} md={4} xl={3}>
            <StatCardServices
              title="Emails Sent"
              newValue={emailCounts}
              icon={Mail}
              color="#2196f3"
              isLoading={isLoading}
            />
          </Grid>
        )}
      </Grid>
      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default DashboardView;
